import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// visx
import { AxisBottom, AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { bisector } from 'd3-array';
import styles from './area-chart.module.scss';

// visx tooltip
import { useTooltip, useTooltipInPortal } from '@visx/tooltip';

// d3-shape
import { area, curveMonotoneX } from 'd3-shape';

// react-spring
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { IconWrapper, getNumberFormatLocale } from '@flexo/general';
import { DateTime } from 'luxon';
import { useLocation } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import { formatDate } from '../helpers';
import { canInterpolatePaths } from './path-interpolation';

/** Replace with your own date formatting logic. */
const ENV = (import.meta as any).env;
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isWebSafari = ENV?.VITE_APP_IS_WEB === 'true' && isSafari;

/**
 * Consolidated chart configuration object that replaces scattered magic numbers
 * throughout the component. This provides:
 *
 * 1. Platform-specific adjustments (web vs mobile)
 * 2. Chart dimension calculations
 * 3. Interaction settings
 * 4. Axis positioning adjustments
 *
 * Benefits:
 * - All magic numbers are documented and centralized
 * - Consistent platform-specific behavior
 * - Easy to maintain and modify dimensions
 * - Clear separation of concerns
 */
const CHART_CONFIG = {
  // Platform-specific adjustments
  web: {
    containerPadding: 45, // Padding applied to container width
    containerHeight: 255, // Chart drawing area height
    svgHeight: 275, // Total SVG height (containerHeight + header space)
    headerMarginTop: 60, // Space for chart header/legend
  },
  mobile: {
    containerPadding: 0, // No padding on mobile (full width)
    containerHeight: 200, // Smaller chart area for mobile
    svgHeight: 220, // Total SVG height for mobile
    headerMarginTop: 40, // Same header space as web
  },
  // Chart dimensions
  chartPadding: {
    right: ENV?.VITE_APP_IS_WEB === 'true' ? 0 : 16, // Extra padding for chart area beyond left margin
    axisExtension: 20, // Extension for chart drawing area beyond axis
  },
  // Interaction and display
  interaction: {
    overlayPadding: { width: 100, height: 50 }, // Extra interaction area
    minVibrationInterval: 50, // Minimum time between haptic feedback
  },
  // Axis positioning adjustments
  axis: {
    bottomLeftOffset: -10, // AxisBottom left position adjustment
    yPadding: 10, // Y-axis padding from top/bottom edges
  },
};

/**
 * Animated Area Component with consistent scaling
 * Uses CSS scaleY transform for smooth vertical collapse/expand animations
 */
function AnimatedAreaClosed<T>({
  data,
  xScale,
  yScale,
  accessors,
  fill,
  stroke,
  fillOpacity,
  curve = curveMonotoneX,
  originalData,
  allKeys,
  currentKey,
  isCollapsing = false,
  baselineY = 0,
}: {
  data: T[];
  xScale: any;
  yScale: any;
  accessors: {
    x: (d: T) => number;
    y: (d: T) => number;
  };
  fill?: string;
  stroke?: string;
  fillOpacity?: number;
  curve?: any;
  originalData?: any[];
  allKeys?: string[];
  currentKey?: string; // The key being rendered (for sharp cut logic)
  isCollapsing?: boolean; // Whether to animate collapse to zero
  baselineY?: number; // Y position of the baseline (yScale(0)) for transform origin
}) {
  // Process data to add sharp cut at start and end
  // Always adds the same number of extension points to maintain consistent path length for animations
  const processedData = useMemo(() => {
    if (!data || !data.length) return data;

    // Find the FIRST point with meaningful (non-zero, non-null) data
    let firstMeaningfulIndex = -1;
    for (let i = 0; i < data.length; i++) {
      const yValue = accessors.y(data[i]);
      if (yValue !== null && yValue !== undefined && yValue !== 0) {
        firstMeaningfulIndex = i;
        break;
      }
    }

    // Find the LAST point with meaningful (non-zero, non-null) data
    let lastMeaningfulIndex = -1;
    for (let i = data.length - 1; i >= 0; i--) {
      const yValue = accessors.y(data[i]);
      if (yValue !== null && yValue !== undefined && yValue !== 0) {
        lastMeaningfulIndex = i;
        break;
      }
    }

    // === Determine if sharp cuts are needed (based on original data, not current visibility) ===
    let shouldCreateLeftSharpCut = false;
    let shouldCreateRightSharpCut = false;

    // Use indices from original data if current data is all zeros (hidden series)
    const useOriginalIndices = firstMeaningfulIndex === -1 || lastMeaningfulIndex === -1;

    if (useOriginalIndices && originalData && currentKey) {
      // Find indices from original data for consistent point count
      for (let i = 0; i < originalData.length; i++) {
        const val = originalData[i][currentKey];
        if (val !== null && val !== undefined && val !== 0) {
          if (firstMeaningfulIndex === -1) firstMeaningfulIndex = i;
          lastMeaningfulIndex = i;
        }
      }
    }

    // If still no meaningful data found, return original data
    if (firstMeaningfulIndex === -1 || lastMeaningfulIndex === -1) return data;

    // Check for LEFT sharp cut
    if (originalData && currentKey && firstMeaningfulIndex > 0) {
      const prevPoint = originalData[firstMeaningfulIndex - 1];
      const valueIsNull = prevPoint[currentKey] === null || prevPoint[currentKey] === undefined;
      shouldCreateLeftSharpCut = valueIsNull;
    }

    // Check for RIGHT sharp cut
    if (originalData && currentKey && lastMeaningfulIndex >= 0) {
      const nextDataIndex = lastMeaningfulIndex + 1;
      if (nextDataIndex < originalData.length) {
        const nextPoint = originalData[nextDataIndex];
        const valueIsNull = nextPoint[currentKey] === null || nextPoint[currentKey] === undefined;
        shouldCreateRightSharpCut = valueIsNull;
      } else {
        shouldCreateRightSharpCut = true;
      }
    } else if (!currentKey) {
      shouldCreateRightSharpCut = true;
    }

    // If no sharp cuts needed, return original data
    if (!shouldCreateLeftSharpCut && !shouldCreateRightSharpCut) {
      return data;
    }

    // Start with all original data
    const resultData = [...data];

    // === ADD LEFT EXTENSION (sharp cut = vertical line from 0 to first value) ===
    if (shouldCreateLeftSharpCut && firstMeaningfulIndex >= 0 && firstMeaningfulIndex < data.length) {
      const referencePoint = data[firstMeaningfulIndex];
      const firstDate = new Date((referencePoint as any).date);
      // Use same date (or very close) to create vertical line
      const extendedDate = new Date(firstDate.getTime() - 1000); // 1 second before

      const leftExtendedPoint = {
        ...referencePoint,
        date: extendedDate.toISOString(),
        // Set Y value to 0 to create vertical sharp cut
        ...(currentKey ? { [currentKey]: 0 } : {}),
      } as T;

      resultData.splice(firstMeaningfulIndex, 0, leftExtendedPoint);
    }

    // === ADD RIGHT EXTENSION (sharp cut = vertical line from last value to 0) ===
    if (shouldCreateRightSharpCut && lastMeaningfulIndex >= 0 && lastMeaningfulIndex < data.length) {
      const adjustedLastIndex = shouldCreateLeftSharpCut ? lastMeaningfulIndex + 1 : lastMeaningfulIndex;
      const referencePoint = data[lastMeaningfulIndex];
      const lastDate = new Date((referencePoint as any).date);
      // Use same date (or very close) to create vertical line
      const extendedDate = new Date(lastDate.getTime() + 1000); // 1 second after

      const rightExtendedPoint = {
        ...referencePoint,
        date: extendedDate.toISOString(),
        // Set Y value to 0 to create vertical sharp cut
        ...(currentKey ? { [currentKey]: 0 } : {}),
      } as T;

      resultData.splice(adjustedLastIndex + 1, 0, rightExtendedPoint);
    }

    return resultData;
  }, [data, accessors, originalData, currentKey]);

  // Current area path
  const pathString = useMemo(() => {
    if (!processedData || !processedData.length) return 'M0,0';

    const areaGenerator = area<T>()
      // Use .defined() to create sharp cuts where data is null/undefined
      // This automatically breaks the area path at gaps in data
      .defined((d) => {
        const y = accessors.y(d);
        return y !== null && y !== undefined;
      })
      .x((d) => xScale(accessors.x(d)))
      .y0(() => yScale(0))
      .y1((d) => yScale(accessors.y(d) ?? 0))
      .curve(curve);

    return areaGenerator(processedData) || 'M0,0';
  }, [processedData, xScale, yScale, accessors, curve]);

  // Baseline path (for animation from zero)
  const zeroPathString = useMemo(() => {
    if (!processedData || !processedData.length) return 'M0,0';

    const areaGenerator = area<T>()
      // Use same .defined() logic for consistent path segments
      .defined((d) => {
        const y = accessors.y(d);
        return y !== null && y !== undefined;
      })
      .x((d) => xScale(accessors.x(d)))
      .y0(() => yScale(0))
      .y1(() => yScale(0))
      .curve(curve);

    return areaGenerator(processedData) || 'M0,0';
  }, [processedData, xScale, yScale, accessors, curve]);

  // Track states for animation logic
  const isInitialMount = useRef(true);
  const wasCollapsing = useRef(false);
  const prevPathRef = useRef(pathString);

  // Spring for scaleY (collapse/expand on timeset change)
  const [scaleSpring, scaleApi] = useSpring(() => ({
    scaleY: isInitialMount.current ? 0 : 1,
    config: { tension: 180, friction: 24 },
  }));

  // Spring for path (animate data changes within same timeset)
  const [pathSpring, pathApi] = useSpring(() => ({
    path: pathString,
    config: { tension: 170, friction: 26 },
  }));

  // EFFECT 1: Handle collapse/expand (timeset change)
  useEffect(() => {
    if (isCollapsing) {
      // Timeset changed: collapse to zero
      wasCollapsing.current = true;
      scaleApi.start({
        to: { scaleY: 0 },
        config: { tension: 200, friction: 22 },
      });
    } else if (wasCollapsing.current) {
      // Just finished collapsing: expand with new data
      wasCollapsing.current = false;
      // Set path immediately (no animation) since we're at scale 0
      pathApi.set({ path: pathString });
      prevPathRef.current = pathString;
      // Then expand
      scaleApi.start({
        to: { scaleY: 1 },
        config: { tension: 170, friction: 26 },
      });
    }
  }, [isCollapsing, pathString, scaleApi, pathApi]);

  // EFFECT 2: Handle path changes within same timeset (NOT during collapse)
  useEffect(() => {
    // Only animate path if:
    // - Not collapsing
    // - Not just finished collapsing (wasCollapsing handles that)
    // - Path actually changed
    if (!isCollapsing && !wasCollapsing.current && prevPathRef.current !== pathString) {
      const prevPath = prevPathRef.current;
      prevPathRef.current = pathString;

      // react-spring's string interpolator throws "The arity of each output value
      // must be equal" when the previous and next `d` strings have a different
      // number of numeric tokens (point count or gap structure changed). Only
      // tween between compatible paths; otherwise snap. The try/catch is a final
      // guard so an unforeseen mismatch can never crash the chart.
      if (canInterpolatePaths(prevPath, pathString)) {
        try {
          pathApi.start({
            to: { path: pathString },
            config: { tension: 170, friction: 26 },
          });
        } catch {
          pathApi.set({ path: pathString });
        }
      } else {
        pathApi.set({ path: pathString });
      }
    }
  }, [pathString, isCollapsing, pathApi]);

  // EFFECT 3: Initial mount animation (grow from zero)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      pathApi.set({ path: pathString });
      prevPathRef.current = pathString;
      scaleApi.start({
        to: { scaleY: 1 },
        config: { tension: 170, friction: 26 },
      });
    }
  }, [scaleApi, pathApi, pathString]);

  const AnimatedG = animated('g');
  const AnimatedPath = animated('path');

  return (
    <AnimatedG
      style={{
        transformOrigin: `0px ${baselineY}px`,
        transform: scaleSpring.scaleY.to((s) => `scaleY(${s})`),
      }}
    >
      <AnimatedPath
        d={pathSpring.path}
        fill={fill}
        fillOpacity={fillOpacity}
        stroke={stroke}
      />
    </AnimatedG>
  );
}

/**
 * Clean Area Chart Component
 */
interface DataPoint {
  date: string;
  [key: string]: any;
}

export const NewAreaChart = ({
  colors = [],
  data = [] as DataPoint[],
  keys = [] as string[],
  title = '',
  unit = '',
  margin = { top: 10, right: 0, bottom: 30, left:  ENV?.VITE_APP_IS_WEB === 'true' ? 35 : 25 },
  containerWidth,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  // Redux
  const calendar = useSelector((state: any) => state.calendar);
  const { selectedTimeSet } = calendar?.hiveCalendar || {
    selectedTimeSet: 'day',
  };
  const kpi = useSelector((state: any) => state.kpi);

  // Determine entity and context from URL
  const pathSegment = location.pathname.split('/')[1];
  let entity = 'Site';
  let kpiContext = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }
  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }

  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  // State management
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({});
  const [selectedData, setSelectedData] = useState<any | null>(null);
  const [interpolatedData, setInterpolatedData] = useState<any | null>(null);
  const [interacting, setInteracting] = useState(false);
  const [width, setWidth] = useState(containerWidth);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionData, setTransitionData] = useState<DataPoint[] | null>(null);

  // Platform-specific configuration
  const isWeb = ENV?.VITE_APP_IS_WEB === 'true';
  const platformConfig = isWeb ? CHART_CONFIG.web : CHART_CONFIG.mobile;

  // Refs for performance optimization
  const lastHoveredDateRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastVibrationTimeRef = useRef(0);
  const prevTimeSetRef = useRef(selectedTimeSet);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Tooltip setup
  const { showTooltip, hideTooltip, tooltipOpen } = useTooltip<any>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal();

  // Ref to store previous sorted data for collapse animation
  const prevSortedDataRef = useRef<DataPoint[]>([]);

  // Handle timeSet changes: save current data and start collapse animation
  useEffect(() => {
    if (prevTimeSetRef.current !== selectedTimeSet) {
      // Save current data for collapse animation (before it changes)
      if (data && data.length > 0) {
        const currentSorted = [...data].sort(
          (a, b) =>
            new Date((a as any).date).getTime() -
            new Date((b as any).date).getTime()
        );
        setTransitionData(currentSorted);
        prevSortedDataRef.current = currentSorted;
      }
      prevTimeSetRef.current = selectedTimeSet;
      setIsTransitioning(true);

      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    }
  }, [selectedTimeSet, data]);

  // Debounce data changes during transition - wait for collapse animation to finish
  useEffect(() => {
    if (isTransitioning && data && data.length > 0) {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Wait for collapse animation to finish before showing new data
      debounceTimerRef.current = setTimeout(() => {
        setTransitionData(null); // Clear transition data
        setIsTransitioning(false);
      }, 1000); // 1000ms debounce for collapse animation
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [data, isTransitioning]);

  // Process and sort data - use OLD data during collapse transition (scaleY handles the animation)
  const sortedData = useMemo(() => {
    // During transition, use the OLD data (transitionData) to maintain shape during scaleY collapse
    if (isTransitioning && transitionData && transitionData.length > 0) {
      return transitionData;
    }

    if (!data || data.length === 0) return [];

    return [...data].sort(
      (a, b) =>
        new Date((a as any).date).getTime() -
        new Date((b as any).date).getTime()
    );
  }, [data, isTransitioning, transitionData]);

  // Find the last point with meaningful data
  const lastMeaningfulDataPoint: DataPoint | null = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return null;

    let lastPoint: DataPoint | null = null;
    for (let i = sortedData.length - 1; i >= 0; i--) {
      if (
        keys.some(
          (key) =>
            visibleKeys[key] &&
            sortedData[i][key] != null &&
            sortedData[i][key] !== 0
        )
      ) {
        lastPoint = sortedData[i];
        break;
      }
    }
    return lastPoint;
  }, [sortedData, keys, visibleKeys]);

  // Check if all data is null/undefined (no data available)
  const isNullData = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return true;
    return sortedData.every((d) =>
      keys.every((key) => d[key] === null || d[key] === undefined)
    );
  }, [sortedData, keys]);

  // Check if all data is zero (or combination of 0 and null)
  const is0ValueData = useMemo(() => {
    if (isNullData || !sortedData || sortedData.length === 0) return false; // If it's null data or no data, it's not 0 value data
    return sortedData.every((d) =>
      keys.every(
        (key) => d[key] === 0 || d[key] === null || d[key] === undefined
      )
    );
  }, [sortedData, keys, isNullData]);

  // Calculate dimensions using configuration
  const innerWidth = width - margin.left - CHART_CONFIG.chartPadding.right;
  const containerHeight = platformConfig.containerHeight;

  // Create unified time scale (extended for chart area)
  const xScale = useMemo(() => {
    if (!sortedData.length)
      return scaleTime({
        domain: [0, 1],
        range: [
          0,
          innerWidth + margin.left + CHART_CONFIG.chartPadding.axisExtension,
        ],
      });

    const dates = sortedData.map((d) => new Date(d.date));
    const domain = [dates[0], dates[dates.length - 1]];

    return scaleTime<number>({
      domain,
      range: [
        margin.left,
        innerWidth + margin.left + CHART_CONFIG.chartPadding.axisExtension,
      ],
    });
  }, [sortedData, width, margin.left, innerWidth]);

  // Create separate scale for the bottom axis (aligned with visible axis)
  const xAxisScale = useMemo(() => {
    if (!sortedData.length)
      return scaleTime({
        domain: [0, 1],
        range: [0, innerWidth],
        nice: true,
      });

    const dates = sortedData.map((d) => new Date(d.date));
    const domain = [dates[0], dates[dates.length - 1]];

    return scaleTime<number>({
      domain,
      range: [margin.left, innerWidth + margin.left],
    });
  }, [sortedData, innerWidth, margin.left]);

  // Y scale
  const yScale = useMemo(() => {
    let minVal = 0;
    let maxVal = 0;
    let hasData = false;

    sortedData.forEach((row) => {
      keys.forEach((k) => {
        if (!visibleKeys[k]) return;
        const val = row[k] ?? 0;
        if (!hasData) {
          minVal = val;
          maxVal = val;
          hasData = true;
        } else {
          if (val < minVal) minVal = val;
          if (val > maxVal) maxVal = val;
        }
      });
    });

    if (!hasData || minVal === maxVal) {
      return scaleLinear<number>({
        domain: [0, 1],
        range: [containerHeight, CHART_CONFIG.axis.yPadding],
      });
    }

    if (minVal > 0) minVal = 0;

    return scaleLinear<number>({
      domain: [minVal, maxVal],
      range: [containerHeight, CHART_CONFIG.axis.yPadding],
      nice: true,
    });
  }, [sortedData, keys, visibleKeys, containerHeight]);

  // Bisector for finding closest data point
  const getDate = (d: any) => new Date(d.date).getTime();
  const dateBisector = bisector(getDate).left;

  // Haptic feedback helper
  const triggerHaptic = useCallback(() => {
    if (isWeb) return;
    const now = Date.now();
    if (
      now - lastVibrationTimeRef.current >=
      CHART_CONFIG.interaction.minVibrationInterval
    ) {
      lastVibrationTimeRef.current = now;
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {
        // Silently handle haptic errors
      });
    }
  }, []);

  // Interpolate data point between two surrounding points
  const interpolateDataPoint = useCallback(
    (xPos: number) => {
      if (!sortedData.length) return null;

      const mouseTime = xScale.invert(xPos).getTime();

      // REMOVED: Special case for extending beyond last meaningful point
      // Now cursor will only show where there's actual drawn area

      const index = dateBisector(sortedData, mouseTime);

      // If we're at the beginning or end, return the actual data point
      if (index === 0) return sortedData[0];
      if (index >= sortedData.length) return sortedData[sortedData.length - 1];

      const leftPoint = sortedData[index - 1];
      const rightPoint = sortedData[index];

      if (!leftPoint || !rightPoint) return sortedData[index] || sortedData[0];

      const leftTime = getDate(leftPoint);
      const rightTime = getDate(rightPoint);

      // Calculate interpolation factor (0 = left point, 1 = right point)
      const factor = (mouseTime - leftTime) / (rightTime - leftTime);
      const clampedFactor = Math.max(0, Math.min(1, factor));

      // Create interpolated data point
      const interpolatedPoint: any = {
        date: new Date(mouseTime).toISOString(),
        x: mouseTime,
      };

      // Interpolate values for each key
      keys.forEach((key) => {
        const leftValue = leftPoint[key] ?? null;
        const rightValue = rightPoint[key] ?? null;
        
        // If either point is null, don't interpolate (respect data boundaries)
        if (leftValue === null || rightValue === null) {
          interpolatedPoint[key] = null;
        } else {
          interpolatedPoint[key] = leftValue + (rightValue - leftValue) * clampedFactor;
        }
      });

      return interpolatedPoint;
    },
    [sortedData, xScale, dateBisector, keys, getDate]
  );

  // Find closest data point (for tooltip data)
  const findClosestDataPoint = useCallback(
    (xPos: number) => {
      if (!sortedData.length) return null;

      const mouseTime = xScale.invert(xPos).getTime();
      const index = dateBisector(sortedData, mouseTime);

      let closest = sortedData[0];
      let minDistance = Math.abs(mouseTime - getDate(sortedData[0]));

      // Check both sides of the bisector
      const candidates = [
        sortedData[Math.max(0, index - 1)],
        sortedData[Math.min(sortedData.length - 1, index)],
      ].filter(Boolean);

      for (const candidate of candidates) {
        const distance = Math.abs(mouseTime - getDate(candidate));
        if (distance < minDistance) {
          minDistance = distance;
          closest = candidate;
        }
      }

      return closest;
    },
    [sortedData, xScale, dateBisector]
  );

  // Unified interaction handler
  const handleInteraction = useCallback(
    (point: { x: number; y: number } | null, isTouchEvent = false) => {
      if (!point) {
        setInteracting(false);
        setSelectedData(null);
        setInterpolatedData(null);
        hideTooltip();
        lastHoveredDateRef.current = null;
        return;
      }

      // Check if we're within the data range (not beyond last meaningful point)
      if (sortedData.length > 0 && lastMeaningfulDataPoint) {
        const lastDataTime = getDate(lastMeaningfulDataPoint);
        const mouseTime = xScale.invert(point.x).getTime();
        
        // Don't allow interaction beyond the last meaningful data point
        // (no extension for cursor, only for drawing)
        if (mouseTime > lastDataTime) {
          setInteracting(false);
          setSelectedData(null);
          setInterpolatedData(null);
          hideTooltip();
          lastHoveredDateRef.current = null;
          return;
        }
      }

      // Get both interpolated data for smooth dots and closest data for tooltip
      const interpolated = interpolateDataPoint(point.x);
      const closest = findClosestDataPoint(point.x);

      if (!interpolated || !closest) return;

      // Check if interpolated data has any valid (non-null, non-zero) visible values
      const hasValidInterpolatedValue = keys.some((key) => {
        return (
          visibleKeys[key] &&
          interpolated[key] !== null &&
          interpolated[key] !== undefined &&
          interpolated[key] !== 0
        );
      });

      // If no valid interpolated values, hide cursor
      if (!hasValidInterpolatedValue) {
        setInteracting(false);
        setSelectedData(null);
        setInterpolatedData(null);
        hideTooltip();
        lastHoveredDateRef.current = null;
        return;
      }

      // Always update interpolated data for smooth dots
      setInterpolatedData(interpolated);
      setInteracting(true);

      // Check if all visible values are zero in closest point
      const hasNonZeroValue = keys.some((key) => {
        return visibleKeys[key] && closest[key] != null && closest[key] !== 0;
      });

      // Check if this is a new selection for tooltip/haptic feedback
      const newDateString = closest.date;
      const isNewSelection = newDateString !== lastHoveredDateRef.current;

      if (isNewSelection) {
        lastHoveredDateRef.current = newDateString;
        setSelectedData(closest);

        // Haptic feedback for new selections
        if (isTouchEvent) {
          triggerHaptic();
        }
      }

      // Only show tooltip if there are non-zero values
      if (hasNonZeroValue) {
        const tooltipX = point.x;
        showTooltip({
          tooltipLeft: tooltipX,
          tooltipTop: 0,
          tooltipData: closest,
        });
      } else {
        hideTooltip();
      }
    },
    [
      interpolateDataPoint,
      findClosestDataPoint,
      xScale,
      showTooltip,
      hideTooltip,
      triggerHaptic,
      sortedData,
      lastMeaningfulDataPoint,
      keys,
      visibleKeys,
      getDate,
    ]
  );

  // Mouse event handlers
  const handleMouseLeave = useCallback(() => {
    setInteracting(false);
    setSelectedData(null);
    setInterpolatedData(null);
    hideTooltip();
    lastHoveredDateRef.current = null;
  }, [hideTooltip]);

  const handleMouseMove = useCallback(
    (evt: React.MouseEvent<SVGRectElement>) => {
      if (!isWeb) return;
      evt.stopPropagation();
      const point = localPoint(evt);

      // Let handleInteraction manage all boundary logic consistently
      handleInteraction(point, false);
    },
    [handleInteraction]
  );

  // Touch event handlers
  const handleTouchEnd = useCallback(() => {
    setInteracting(false);
    setSelectedData(null);
    setInterpolatedData(null);
    hideTooltip();
    lastHoveredDateRef.current = null;
  }, [hideTooltip]);

  const handleTouchStart = useCallback(
    (evt: React.TouchEvent<SVGRectElement>) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (evt.touches.length > 0) {
        const point = localPoint(evt.target as any, evt.touches[0] as any);
        handleInteraction(point, true);
      }
    },
    [handleInteraction]
  );

  const handleTouchMove = useCallback(
    (evt: React.TouchEvent<SVGRectElement>) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (evt.changedTouches.length > 0) {
        const point = localPoint(
          evt.target as any,
          evt.changedTouches[0] as any
        );

        // Let handleInteraction manage all boundary logic consistently
        handleInteraction(point, true);
      }
    },
    [handleInteraction]
  );

  // Legend toggle
  const toggleKeyVisibility = useCallback(
    (key: string) => {
      if (is0ValueData || isNullData) return;

      const visibleCount = Object.values(visibleKeys).filter(Boolean).length;
      if (visibleKeys[key] && visibleCount === 1) return; // Prevent hiding last key

      setVisibleKeys((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    },
    [visibleKeys, is0ValueData, isNullData]
  );

  const generateTickValues = useCallback(() => {
    if (!sortedData.length) return [];

    const dates = sortedData.map((d) => new Date(d.date));

    // Generate ticks based on selectedTimeSet
    if (selectedTimeSet === 'day') {
      // For day view: show hours (e.g., 0:00, 6:00, 12:00, 18:00)
      const numTicks = Math.min(5, dates.length);
      if (numTicks <= 1) return dates;
      const step = Math.max(1, Math.floor(dates.length / (numTicks - 1)));
      const ticks: Date[] = [];
      for (let i = 0; i < numTicks - 1; i++) {
        ticks.push(dates[i * step]);
      }
      ticks.push(dates[dates.length - 1]);
      return ticks;
    }

    if (selectedTimeSet === 'week') {
      // For week view: show days (e.g., Mon, Tue, Wed...)
      const numTicks = Math.min(7, dates.length);
      if (numTicks <= 1) return dates;
      const step = Math.max(1, Math.floor(dates.length / (numTicks - 1)));
      const ticks: Date[] = [];
      for (let i = 0; i < numTicks - 1; i++) {
        ticks.push(dates[i * step]);
      }
      ticks.push(dates[dates.length - 1]);
      return ticks;
    }

    if (selectedTimeSet === 'month') {
      // For month view: show days distributed evenly (e.g., 1, 5, 10, 15, 20, 25, 30)
      const numTicks = Math.min(7, dates.length);
      if (numTicks <= 1) return dates;
      const step = Math.max(1, Math.floor(dates.length / (numTicks - 1)));
      const ticks: Date[] = [];
      for (let i = 0; i < numTicks - 1; i++) {
        ticks.push(dates[i * step]);
      }
      ticks.push(dates[dates.length - 1]);
      return ticks;
    }

    // For year view: show months
    const uniqueMonths = new Map<string, Date>();
    dates.forEach((date) => {
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      if (!uniqueMonths.has(monthKey)) {
        uniqueMonths.set(monthKey, new Date(date.getFullYear(), date.getMonth(), 1));
      }
    });

    const monthTicks = Array.from(uniqueMonths.values());

    if (monthTicks.length <= 6) {
      return monthTicks;
    }

    const numTicks = 6;
    const step = Math.floor(monthTicks.length / (numTicks - 1));
    const ticks: Date[] = [];
    for (let i = 0; i < numTicks - 1; i++) {
      ticks.push(monthTicks[i * step]);
    }
    ticks.push(monthTicks[monthTicks.length - 1]);

    return ticks;
  }, [sortedData, selectedTimeSet]);

  // Color helper
  const getGreyscaleColor = useCallback((color: string) => {
    if (!color?.includes('#')) return color;

    const red = parseInt(color.substr(1, 2), 16);
    const green = parseInt(color.substr(3, 2), 16);
    const blue = parseInt(color.substr(5, 2), 16);
    const grayscale = Math.round(0.299 * red + 0.587 * green + 0.114 * blue);
    const grayscaleHex = grayscale.toString(16).padStart(2, '0');

    return '#' + grayscaleHex + grayscaleHex + grayscaleHex + '60';
  }, []);

  // Initialize visible keys
  useEffect(() => {
    if (keys.length > 0) {
      const init = keys.reduce((acc, k) => {
        acc[k] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setVisibleKeys(init);
    }
  }, [keys]);

  // Update width on container changes
  useEffect(() => {
    if (containerWidth > 0) {
      setWidth(containerWidth - platformConfig.containerPadding);
    }
  }, [containerWidth, platformConfig.containerPadding]);

  // Header title helper
  const getTitleDetail = () => {
    if (!selectedData) return '';
    return formatDate(selectedData.date, selectedTimeSet);
  };

  // Animated hover line component
  const AnimatedHoverLine = ({
    x,
    y1,
    y2,
  }: {
    x: number;
    y1: number;
    y2: number;
  }) => {
    const [spring, api] = useSpring(() => ({
      x1: x,
      x2: x,
      config: { tension: 300, friction: 30 },
    }));

    useEffect(() => {
      api.start({ x1: x, x2: x });
    }, [api, x]);

    const AnimatedLine = animated('line');

    return (
      <AnimatedLine
        {...spring}
        y1={y1}
        y2={y2}
        stroke="white"
        strokeWidth={2}
        pointerEvents="none"
      />
    );
  };

  // Animated dots component
  const AnimatedDot = ({
    keyName,
    color,
    x,
    y,
  }: {
    keyName: string;
    color: string;
    x: number;
    y: number;
  }) => {
    const [spring, api] = useSpring(() => ({
      cx: x,
      cy: y,
      config: { tension: 300, friction: 30 },
    }));

    useEffect(() => {
      api.start({ cx: x, cy: y });
    }, [api, x, y]);

    const AnimatedCircle = animated('circle');

    return (
      <AnimatedCircle
        {...spring}
        r={6}
        fill={color}
        stroke="white"
        strokeWidth={2}
      />
    );
  };

  // Render dots for interpolated data point
  const renderDots = () => {
    if (!interpolatedData || !interacting) return null;

    // Check if all visible values are zero
    const hasNonZeroValue = keys.some((key) => {
      return (
        visibleKeys[key] &&
        interpolatedData[key] != null &&
        interpolatedData[key] !== 0
      );
    });

    if (!hasNonZeroValue) return null;

    return keys
      .filter(
        (key) =>
          visibleKeys[key] &&
          interpolatedData[key] !== null &&
          interpolatedData[key] !== undefined &&
          interpolatedData[key] !== 0
      )
      .map((key, index) => {
        const i = keys.indexOf(key);
        const cx = xScale(
          interpolatedData.x || new Date(interpolatedData.date)
        );
        const cy = yScale(interpolatedData[key]);

        return (
          <AnimatedDot
            key={`dot-${key}`}
            keyName={key}
            color={colors[i]}
            x={cx}
            y={cy}
          />
        );
      });
  };

  return (
    <div
      style={
        !isWeb
          ? {
              position: 'relative',
              width: '100%',
              height: '100%',
            }
          : {}
      }
      ref={containerRef}
    >
      {/* Header */}
      <div style={{ marginBottom: '6px', marginLeft: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {selectedData && (
            <div
              className="heading4"
              style={{ color: '#001117', marginRight: '.5em' }}
            >
              {getTitleDetail()}
            </div>
          )}
          <div
            style={{ color: '#001117' }}
            className={selectedData ? 'heading4L' : 'heading4'}
          >
            {t(title)}
          </div>
        </div>

        {/* Legend */}
        <div style={{ height: '40px' }}>
          {keys.map((k, i) => {
            const isVisible = visibleKeys[k];
            const activeValue =
              isVisible && selectedData && selectedData[k] !== undefined
                ? selectedData[k]
                : undefined;

            return (
              <button
                key={k}
                style={{
                  border: 'none',
                  color: '#505558',
                  display: 'inline-block',
                  borderBottom: `4px solid ${
                    isVisible ? colors[i] : '#F7F7F7'
                  }`,
                  background: 'none',
                  marginRight: '1em',
                  textAlign: 'left',
                  cursor: isNullData || is0ValueData ? 'default' : 'pointer',
                  padding: '0',
                }}
                className="detail"
                onClick={() => toggleKeyVisibility(k)}
              >
                {t(k)}{' '}
                {activeValue !== undefined && interacting ? (
                  <strong style={{ fontWeight: isWebSafari ? 500 : 600 }}>
                    | {activeValue?.toLocaleString(getNumberFormatLocale())} {unit}
                  </strong>
                ) : (
                  ''
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <ParentSize>
        {({ width: parentWidth, height: parentHeight }) => (
          <svg
            width={ ENV?.VITE_APP_IS_WEB === 'true' ? parentWidth : innerWidth}
            height={platformConfig.svgHeight}
            style={{
              overflow: 'visible',
              marginTop: platformConfig.headerMarginTop,
            }}
          >
            {/* X Axis */}
            <AxisBottom
              top={(containerHeight + ( isWeb ? 5 : 0 ) )}
              left={margin.left + CHART_CONFIG.axis.bottomLeftOffset}
              scale={xAxisScale}
              numTicks={4}
              tickValues={generateTickValues()}
              stroke="#F7F7F7"
              tickStroke="transparent"
              tickFormat={(value, index, ticks) => {
                // Custom formatting for the last tick based on time range
                if (index === ticks.length - 1) {
                  if (selectedTimeSet === 'day') {
                    return '23:59';
                  } else if (selectedTimeSet === 'year') {
                    return 'Dec';
                  } else {
                    // For other time ranges (week, month, etc.), show the last day/date
                    const date = DateTime.fromJSDate(new Date(value as any));
                    const isoString = date.toISO();
                    return formatDate(isoString || '', selectedTimeSet);
                  }
                }
                const date = DateTime.fromJSDate(new Date(value as any));
                const isoString = date.toISO();
                return formatDate(isoString || '', selectedTimeSet);
              }}
              tickLabelProps={() => ({
                fontSize: 12,
                fill: '#838B93',
                textAnchor: 'middle',
                dx: 0,
              })}
              tickClassName="detail"
            />

            {/* Y Axis */}
            <AxisLeft
              scale={yScale}
              numTicks={is0ValueData || isNullData ? 0 : 4}
              tickFormat={(tick) =>
                tick.toLocaleString(getNumberFormatLocale(), {
                  maximumFractionDigits: 1,
                })
              }
              stroke="#F7F7F7"
              tickStroke="transparent"
              left={margin.left}
              tickLabelProps={() => ({
                fontSize: 12,
                fill: '#838B93',
                textAnchor: 'end',
                opacity: is0ValueData || isNullData ? 0 : 1,
              })}
              label={unit}
              labelProps={{
                x: 0,
                y: isWeb ? -30 :-10,
                fill: '#838B93',
                fontSize: 12,
                fontWeight: isWebSafari ? 400 : 600,
                textAnchor: 'end',
              }}
              labelClassName={`detail ${styles['rotate']} ${styles['rotate__360']}`}
              tickClassName="detail"
            />

            {/* Areas */}
            {(width > 0 && isLoading) || (width > 0 && isNullData && !isTransitioning) || (width > 0 && is0ValueData && !isTransitioning) ? (
              // Did some research and foreignObject should not bring issues since it has browser and mobile support with ionic.
              <foreignObject
                x={0}
                y={0}
                width={ENV?.VITE_APP_IS_WEB === 'true' ? parentWidth : (width + margin.left + margin.right)}
                height={containerHeight}
                style={{
                  pointerEvents: 'none', // so the axes remain interactive if needed
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.9,
                  }}
                  className={styles.Areachart__NoData}
                >
                  <IconWrapper
                    iconName={isLoading ? 'loading' : is0ValueData ? 'ellipse' : 'no_data_chart'}
                    style={{ marginBottom: 8 }}
                    className={styles.Areachart__NoData__Icon}
                  />
                  <div className={`${styles.Areachart__NoData__Label} detail`} style={{ width: '70%' }}>
                    {isLoading
                      ? t('charts.loading')
                      : is0ValueData
                      ? t('charts.everything_is_zero')
                      : t('charts.data_unavailable')}
                  </div>
                </div>
              </foreignObject>
            ) : (
              <>
                {keys.map((key, i) => {
                  // Keep null values to enable .defined() in area generator for sharp cuts
                  const keyData = sortedData.map((d) => ({
                    date: d.date,
                    [key]: visibleKeys[key] ? d[key] : null,
                  }));

                  return (
                    <AnimatedAreaClosed
                      // Key is stable - scaleY animation handles transitions smoothly
                      // Old data collapses vertically to baseline, then new data expands from baseline
                      key={key}
                      data={keyData}
                      xScale={xScale}
                      yScale={yScale}
                      accessors={{
                        x: (d: any) => new Date(d.date).getTime(),
                        y: (d: any) => d[key] ?? 0,
                      }}
                      fill={
                        interacting ? getGreyscaleColor(colors[i]) : colors[i]
                      }
                      fillOpacity={0.5}
                      originalData={sortedData}
                      allKeys={keys}
                      currentKey={key}
                      isCollapsing={isTransitioning}
                      baselineY={yScale(0)}
                    />
                  );
                })}

                {/* Hover line and dots */}
                {interpolatedData &&
                  interacting &&
                  (() => {
                    // Check if all visible values are zero
                    const hasNonZeroValue = keys.some((key) => {
                      return (
                        visibleKeys[key] &&
                        interpolatedData[key] != null &&
                        interpolatedData[key] !== 0
                      );
                    });

                    if (!hasNonZeroValue) return null;

                    return (
                      <>
                        <AnimatedHoverLine
                          x={xScale(
                            interpolatedData.x ||
                              new Date(interpolatedData.date)
                          )}
                          y1={CHART_CONFIG.axis.yPadding}
                          y2={containerHeight + 15}
                        />
                        {renderDots()}
                      </>
                    );
                  })()}

                {/* Interaction overlay */}
                <rect
                  x={0}
                  y={0}
                  width={
                    innerWidth + CHART_CONFIG.interaction.overlayPadding.width
                  }
                  height={
                    containerHeight +
                    CHART_CONFIG.interaction.overlayPadding.height
                  }
                  fill="transparent"
                  style={{
                    touchAction: 'none',
                    userSelect: 'none',
                  }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
              </>
            )}
          </svg>
        )}
      </ParentSize>
    </div>
  );
};

export default NewAreaChart;
